import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Shield, 
  Eye, 
  Activity, 
  Lock, 
  User, 
  FileText,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  Grid3X3,
} from 'lucide-react';
import { Card } from '../ui/Card'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { ThreatDetectionDashboard } from './threat-detection';
import { AuthenticationDashboard } from './auth-monitoring';
import { KeyManagementDashboard } from './crypto-ops';
import { SecurityAuditDashboard, ComplianceManagement } from './audit-compliance';
import { useSecurityDashboard, useSecurityWebSocket } from '../../hooks/useSecurity'
import { cn } from '../../utils/cn'
import { formatRelativeTime } from '../../utils/format'

// GH #2704: the 'crypto' view is gone. Its panel read GET /crypto/metrics,
// whose every figure came from a key-rotation route that rotated nothing --
// socket-cloud-api holds no key material. Removed rather than gated; nothing
// replaces it. Key Management ('keys') is a separate surface and is unaffected.
type SOCView = 'overview' | 'threats' | 'auth' | 'keys' | 'audit' | 'compliance'

interface SOCModule {
  id: SOCView
  name: string
  icon: React.ElementType
  description: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  alerts?: number
}

const socModules: SOCModule[] = [
  {
    id: 'threats',
    name: 'Threat Detection',
    icon: Shield,
    description: 'ML-powered threat detection and anomaly analysis',
    priority: 'critical',
    alerts: 0,
  },
  {
    id: 'auth',
    name: 'Authentication',
    icon: User,
    description: 'Authentication monitoring and access tracking',
    priority: 'high',
    alerts: 0,
  },
  {
    id: 'keys',
    name: 'Key Management',
    icon: Lock,
    description: 'Cryptographic key and certificate lifecycle',
    priority: 'medium',
  },
  {
    id: 'audit',
    name: 'Security Audit',
    icon: FileText,
    description: 'Comprehensive audit logging and event tracking',
    priority: 'high',
  },
  {
    id: 'compliance',
    name: 'Compliance',
    icon: CheckCircle2,
    description: 'Regulatory compliance monitoring and reporting',
    priority: 'medium',
  },
];

export const SecurityOperationsCenter: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial view from URL or default to 'overview'
  const getInitialView = () => {
    const view = searchParams.get('view');
    const validViews = ['overview', 'threats', 'auth', 'keys', 'audit', 'compliance'];
    if (view && validViews.includes(view)) {
      return view as SOCView;
    }
    return 'overview';
  };

  const [currentView, setCurrentView] = useState<SOCView>(getInitialView());
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const [refreshInterval] = useState(30000); // 30 seconds

  const { data: dashboardData, isLoading } = useSecurityDashboard(refreshInterval);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isViewLoading, setIsViewLoading] = useState(false);

  useEffect(() => {
    if (!isLoading && dashboardData) {
      setIsInitialLoad(false);
    }
  }, [isLoading, dashboardData]);

  // Handle view changes with loading state
  const handleViewChange = useCallback((view: SOCView) => {
    setIsViewLoading(true);
    setCurrentView(view);
    setSearchParams({ view });
    // Simulate view loading delay
    setTimeout(() => setIsViewLoading(false), 300);
  }, [setSearchParams]);

  // Real-time WebSocket connection for security events
  const handleSecurityEvent = useCallback((_event: any) => {
    console.log('Security event received:', _event);
    // Handle real-time security events here
    // You could show notifications, update counters, etc.
  }, []);

  useSecurityWebSocket(handleSecurityEvent);

  const handleModuleClick = (moduleId: SOCView) => {
    if (currentView === 'overview') {
      handleViewChange(moduleId);
    } else {
      handleViewChange('overview');
    }
  };

  // Sync with URL changes
  useEffect(() => {
    const view = searchParams.get('view');
    const validViews = ['overview', 'threats', 'auth', 'keys', 'audit', 'compliance'];
    if (view && validViews.includes(view)) {
      setCurrentView(view as SOCView);
    } else if (!view) {
      // If no view param, set it to overview in URL
      setSearchParams({ view: 'overview' });
      setCurrentView('overview');
    }
  }, [searchParams, setSearchParams]);

  const handleExpandModule = (moduleId: string) => {
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
  };

  const handleExportSOCReport = () => {
    console.log('Exporting comprehensive SOC report...');
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* SOC Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="skeleton w-10 h-10 rounded-lg" />
            <div>
              <div className="skeleton h-7 w-80 mb-2" />
              <div className="skeleton h-4 w-96" />
            </div>
          </div>
          <div className="skeleton w-40 h-10 rounded-lg" />
        </div>

        {/* Security Status Banner Skeleton */}
        <div className="skeleton h-32 rounded-xl" />

        {/* Module Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="skeleton h-48 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // Show loading state for initial load
  if (isInitialLoad && isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[600px] space-y-4">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
          <Shield className="absolute inset-0 m-auto w-8 h-8 text-primary-600" />
        </div>
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-console-text">Loading Security Operations Center</h3>
          <p className="text-sm text-console-muted">Initializing security modules and threat detection systems...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* SOC Header */}
      <div className="flex items-center justify-between">
        <div 
          className={cn(
            "flex items-center gap-3",
            currentView !== 'overview' && "cursor-pointer hover:opacity-80 transition-opacity"
          )}
          onClick={() => {
            if (currentView !== 'overview') {
              handleViewChange('overview');
            }
          }}
        >
          <div className="p-2 bg-gradient-to-r from-red-600 to-orange-600 rounded-lg">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl suse-bold text-console-text">Security Operations Center</h1>
            <p className="text-console-muted">
              {currentView === 'overview' 
                ? 'Enterprise-grade security monitoring and threat response'
                : `${socModules.find(m => m.id === currentView)?.name || 'Security Module'}`
              }
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {currentView !== 'overview' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleViewChange('overview')}
            >
              <Grid3X3 className="w-4 h-4 mr-2" />
              Overview
            </Button>
          )}
          
          <Button variant="secondary" size="sm" onClick={handleExportSOCReport}>
            <Download className="w-4 h-4 mr-2" />
            Export SOC Report
          </Button>
        </div>
      </div>

      {/* Security Status Banner - Only show on overview */}
      {currentView === 'overview' && (
        <Card className="p-6 bg-gradient-to-r from-green-600/10 to-blue-600/10 border-green-600/30">
          <div className="space-y-4">
            {/* Security Posture Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-status-online rounded-full animate-pulse" />
                <span className="text-xl font-semibold text-console-text">Security Posture: SECURE</span>
              </div>
              <div className="text-sm text-console-muted">
                Last updated: {formatRelativeTime(new Date())}
              </div>
            </div>
            
            {/* Combined Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
              {/* Core Security Metrics */}
              <div className="flex items-center gap-2">
                <div className="p-2 bg-status-online/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-status-online" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-console-text">
                    {dashboardData?.threatDetection?.overview?.activeThreats || 0}
                  </div>
                  <div className="text-sm text-console-muted">Active Threats</div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <div className="p-2 bg-primary-600/20 rounded-lg">
                  <Activity className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-console-text">
                    {dashboardData?.authentication?.overview?.successRate?.toFixed(1) || '0'}%
                  </div>
                  <div className="text-sm text-console-muted">Auth Success</div>
                </div>
              </div>
              
              {/* Additional Metrics from Quick Stats */}
              <div className="flex items-center gap-2">
                <div className="p-2 bg-status-online/20 rounded-lg">
                  <Shield className="w-5 h-5 text-status-online" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-console-text">
                    {dashboardData?.threatDetection?.overview.detectionAccuracy?.toFixed(0) || '0'}
                  </div>
                  <div className="text-sm text-console-muted">Threat Score</div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <div className="p-2 bg-primary-600/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-console-text">
                    {dashboardData?.compliance?.overview.complianceScore?.toFixed(0) || '0'}%
                  </div>
                  <div className="text-sm text-console-muted">Compliance</div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <div className="p-2 bg-secondary-600/20 rounded-lg">
                  <User className="w-5 h-5 text-secondary-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-console-text">
                    {dashboardData?.identity?.overview.verifiedIdentities || 0}
                  </div>
                  <div className="text-sm text-console-muted">Identities</div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <div className="p-2 bg-warning-600/20 rounded-lg">
                  <FileText className="w-5 h-5 text-warning-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-console-text">
                    {(dashboardData?.compliance?.overview.auditEvents || 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-console-muted">Audit Events</div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Content Area */}
      {currentView === 'overview' ? (
        <div className="space-y-6">
          {/* SOC Module Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {socModules.map((module) => (
              <Card
                key={module.id}
                className={cn(
                  'p-6 cursor-pointer transition-all duration-200 hover:shadow-lg group',
                  expandedModule === module.id && 'ring-2 ring-primary-600',
                )}
                onClick={() => handleModuleClick(module.id)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={cn(
                    'p-3 rounded-lg transition-colors',
                    module.priority === 'critical' ? 'bg-danger-600/20 group-hover:bg-danger-600/30' :
                    module.priority === 'high' ? 'bg-warning-600/20 group-hover:bg-warning-600/30' :
                    module.priority === 'medium' ? 'bg-primary-600/20 group-hover:bg-primary-600/30' :
                    'bg-secondary-600/20 group-hover:bg-secondary-600/30',
                  )}>
                    <module.icon className={cn(
                      'w-6 h-6',
                      module.priority === 'critical' ? 'text-danger-600' :
                      module.priority === 'high' ? 'text-warning-600' :
                      module.priority === 'medium' ? 'text-primary-600' :
                      'text-secondary-600',
                    )} />
                  </div>
                  
                  {module.alerts !== undefined && module.alerts > 0 && (
                    <Badge variant="severity" severity="critical">
                      {module.alerts}
                    </Badge>
                  )}
                </div>
                
                <h3 className="text-lg font-semibold text-console-text mb-2 group-hover:text-primary-600 transition-colors">
                  {module.name}
                </h3>
                
                <p className="text-sm text-console-muted mb-4">
                  {module.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <Badge 
                    variant="severity"
                    severity={
                      module.priority === 'critical' ? 'critical' :
                      module.priority === 'high' ? 'warning' :
                      'info'
                    }
                  >
                    {module.priority}
                  </Badge>
                  
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExpandModule(module.id);
                    }}
                  >
                    <Eye className="w-3 h-3" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {/* Recent Security Events */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-console-text">Recent Security Events</h3>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-console-muted" />
                <span className="text-sm text-console-muted">Last 24 hours</span>
              </div>
            </div>
            
            <div className="space-y-3">
              {dashboardData?.recentEvents?.threats?.slice(0, 3).map((threat) => (
                <div key={threat.id} className="flex items-center gap-3 p-3 bg-console-surface/30 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-danger-600" />
                  <div className="flex-1">
                    <div className="font-medium text-console-text">
                      {threat.type.replace('_', ' ').toUpperCase()} detected
                    </div>
                    <div className="text-sm text-console-muted">
                      Node: {threat.nodeId} | Risk: {threat.riskScore}
                    </div>
                  </div>
                  <span className="text-xs text-console-muted">
                    {formatRelativeTime(threat.timestamp)}
                  </span>
                </div>
              )) || []}
              
              {dashboardData?.recentEvents?.authEvents?.slice(0, 2).map((auth) => (
                <div key={auth.id} className="flex items-center gap-3 p-3 bg-console-surface/30 rounded-lg">
                  <User className="w-5 h-5 text-primary-600" />
                  <div className="flex-1">
                    <div className="font-medium text-console-text">
                      Authentication {auth.success ? 'success' : 'failure'}: {auth.user}
                    </div>
                    <div className="text-sm text-console-muted">
                      Action: {auth.action} | IP: {auth.ip}
                    </div>
                  </div>
                  <span className="text-xs text-console-muted">
                    {formatRelativeTime(auth.timestamp)}
                  </span>
                </div>
              )) || []}
            </div>
          </Card>
        </div>
      ) : (
        <div className="space-y-6 relative">
          {/* Loading overlay for view transitions */}
          {isViewLoading && (
            <div className="absolute inset-0 bg-console-background/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg min-h-[400px]">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="text-console-muted">Loading {socModules.find(m => m.id === currentView)?.name || 'module'}...</span>
              </div>
            </div>
          )}
          
          {/* Module-specific content */}
          {currentView === 'threats' && <ThreatDetectionDashboard />}
          {currentView === 'auth' && <AuthenticationDashboard />}
          {currentView === 'keys' && <KeyManagementDashboard />}
          {currentView === 'audit' && <SecurityAuditDashboard />}
          {currentView === 'compliance' && <ComplianceManagement />}
        </div>
      )}
    </div>
  );
};